/**
 * API Request Cache (DC-1370)
 *
 * Module-level cache for GET request deduplication and response caching.
 * Lives outside React so cache persists across component re-mounts.
 *
 * Features:
 * - In-flight request deduplication (prevents duplicate simultaneous requests)
 * - TTL-based response caching (default 5 minutes)
 * - Pattern-based cache invalidation
 * - Automatic mutation invalidation (POST/PUT/PATCH/DELETE clear related caches)
 */

// ============================================================================
// TYPES
// ============================================================================

interface CacheEntry<T = unknown> {
  data: T;
  timestamp: number;
  ttl: number;
}

// ============================================================================
// CACHE STORES
// ============================================================================

/** Completed response cache: cacheKey -> { data, timestamp, ttl } */
const responseCache = new Map<string, CacheEntry>();

/** In-flight request deduplication: cacheKey -> Promise */
const inFlightRequests = new Map<string, Promise<unknown>>();

// ============================================================================
// ORGANIZATION EPOCH (CC-V35-BANK-PROFILE-01, design §5.9)
// ============================================================================

/**
 * Monotonic epoch advanced at every organization boundary
 * (`crossOrganizationBoundary` in hooks/organizationBoundary.ts).
 *
 * Every cache key embeds the current epoch, so organization-scoped entries
 * cached before a boundary become UNREACHABLE — not merely stale — after it.
 * Invalidation-by-enumeration fails the moment someone adds a cache and
 * forgets the list; keying by epoch means a cache added later inherits the
 * property by construction instead of by discipline.
 */
let organizationEpoch = 0;

/** Advance the epoch. Called ONLY by the organization boundary. */
export function advanceOrganizationEpoch(): void {
  organizationEpoch += 1;
}

// ============================================================================
// CACHE KEY
// ============================================================================

/**
 * Generate a deterministic cache key from URL and query params, scoped to
 * the current organization epoch (§5.9).
 * Only used for GET requests (mutations are never cached).
 */
export function getCacheKey(url: string): string {
  return `GET:${organizationEpoch}:${url}`;
}

// ============================================================================
// CACHE OPERATIONS
// ============================================================================

/**
 * Get a cached response if it exists and hasn't expired.
 */
export function getCachedResponse<T>(key: string): T | undefined {
  const entry = responseCache.get(key);
  if (!entry) return undefined;

  const age = Date.now() - entry.timestamp;
  if (age > entry.ttl) {
    responseCache.delete(key);
    return undefined;
  }

  return entry.data as T;
}

/**
 * Store a response in the cache.
 */
export function setCachedResponse<T>(key: string, data: T, ttl: number): void {
  responseCache.set(key, { data, timestamp: Date.now(), ttl });
}

/**
 * Get an in-flight request promise for deduplication.
 */
export function getInFlightRequest<T>(key: string): Promise<T> | undefined {
  return inFlightRequests.get(key) as Promise<T> | undefined;
}

/**
 * Register an in-flight request for deduplication.
 * Automatically removes itself when the promise resolves/rejects.
 */
export function setInFlightRequest<T>(key: string, promise: Promise<T>): Promise<T> {
  inFlightRequests.set(key, promise);

  const cleanup = () => {
    // Only remove if it's still the same promise (not replaced by a newer request)
    if (inFlightRequests.get(key) === promise) {
      inFlightRequests.delete(key);
    }
  };

  promise.then(cleanup, cleanup);
  return promise;
}

/**
 * Invalidate cache entries matching a URL pattern.
 *
 * @param pattern - String prefix or RegExp to match against cache keys.
 *   If string: matches keys that contain the string (e.g. '/api/journeys')
 *   If RegExp: matches keys against the regex
 *   If omitted: clears entire cache
 */
export function invalidateCache(pattern?: string | RegExp): void {
  if (!pattern) {
    responseCache.clear();
    return;
  }

  for (const key of responseCache.keys()) {
    const matches =
      typeof pattern === 'string'
        ? key.includes(pattern)
        : pattern.test(key);

    if (matches) {
      responseCache.delete(key);
    }
  }
}

/**
 * Invalidate caches related to a mutation URL.
 * Called automatically after POST/PUT/PATCH/DELETE.
 *
 * Strategy: Invalidate any cached GET that shares the same base path.
 * e.g. POST /api/journeys/123/action-plan invalidates GET /api/journeys/123/*
 */
export function invalidateRelatedCaches(mutationUrl: string): void {
  // Extract base path segments (e.g. /api/journeys/123 from /api/journeys/123/action-plan)
  const segments = mutationUrl.split('/').filter(Boolean);

  // Invalidate progressively: try full path first, then parent paths
  // This catches both exact matches and parent collection caches
  for (let i = segments.length; i >= 2; i--) {
    const prefix = '/' + segments.slice(0, i).join('/');
    invalidateCache(prefix);
  }
}

/**
 * Get cache statistics for debugging.
 */
export function getCacheStats(): {
  cachedEntries: number;
  inFlightRequests: number;
} {
  return {
    cachedEntries: responseCache.size,
    inFlightRequests: inFlightRequests.size,
  };
}
